<template>
  <form action="#" class="mb-40" @submit.prevent="addFirstResult">
    <div class="row gx-xxl-6">
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ t("transfer_page.reference_no") }} <span class="text-danger">*</span></label>
          <input
            type="text"
            class="transfer-field w-100 h-55 rounded-1 fs-14 text-black px-3"
            :value="referenceNo"
            readonly
          />
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ t("transfer_page.from_warehouse") }} <span class="text-danger">*</span></label>
          <select
            class="transfer-field rounded-1 fs-14 text-optional w-100 h-55 px-3"
            :value="fromWarehouseId"
            :disabled="fromWarehouseLocked || isLoading"
            @change="$emit('update:fromWarehouseId', ($event.target as HTMLSelectElement).value ? Number(($event.target as HTMLSelectElement).value) : null)"
          >
            <option :value="null">{{ t("transfer_page.select_warehouse") }}</option>
            <option v-for="w in warehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
          </select>
          <small v-if="fromWarehouseLocked" class="text-muted d-block mt-2">
            {{ t("transfer_page.source_warehouse_locked") }}
          </small>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ t("transfer_page.to_warehouse") }} <span class="text-danger">*</span></label>
          <select
            class="transfer-field rounded-1 fs-14 text-optional w-100 h-55 px-3"
            :value="toWarehouseId"
            :disabled="isLoading"
            @change="$emit('update:toWarehouseId', ($event.target as HTMLSelectElement).value ? Number(($event.target as HTMLSelectElement).value) : null)"
          >
            <option :value="null">{{ t("transfer_page.select_warehouse") }}</option>
            <option v-for="w in availableDestinationWarehouses" :key="w.id" :value="w.id">{{ w.name }}</option>
          </select>
        </div>
      </div>
      <div class="col-lg-4">
        <div class="form-group mb-25">
          <label class="d-block fs-14 text-black mb-2">{{ t("transfer_page.transfer_date") }}</label>
          <input
            type="date"
            class="transfer-field w-100 h-55 rounded-1 fs-14 text-black px-3"
            :value="date"
            readonly
          />
        </div>
      </div>
      <div class="col-12">
        <div class="form-group position-relative">
          <label class="d-block fs-14 text-black mb-2">{{ t("transfer_page.choose_product") }}</label>
          <div class="transfer-search-box position-relative w-100">
            <input
              ref="searchInput"
              type="text"
              :placeholder="t('transfer_page.scan_search_product')"
              class="transfer-search-input"
              v-model="searchQuery"
              @input="$emit('search-product', searchQuery)"
              @focus="isFocused = true"
              @keyup.enter.prevent="addFirstResult"
              autocomplete="off"
            />
            <span class="search-icon" aria-hidden="true">
              <i class="ri-search-line"></i>
            </span>
          </div>

          <div v-if="shouldShowResults" class="search-results-dropdown mt-2">
            <div class="result-list-header">
              <span>{{ normalizedQuery.length ? t("transfer_page.matching_products") : t("transfer_page.available_products") }}</span>
              <small>{{ t("transfer_page.items_count", { count: searchResults.length }) }}</small>
            </div>

            <button
              v-for="product in searchResults"
              :key="product.id"
              type="button"
              class="result-item"
              @click="selectProduct(product)"
            >
              <div class="result-main">
                <strong class="result-name">{{ product.name }}</strong>
                <small class="result-meta">
                  {{ product.category?.name || t("transfer_page.no_category") }}
                  <span class="meta-dot">•</span>
                  {{ sourceWarehouseName }}
                </small>
              </div>
              <div class="result-side">
                <span class="result-code">{{ product.code }}</span>
                <span class="result-stock" :class="{ 'is-low': getAvailableStock(product) <= 0 }">
                  {{ t("transfer_page.stock") }}: {{ getAvailableStock(product) }}
                </span>
              </div>
            </button>

            <div v-if="searchResults.length === 0" class="result-empty-state">
              {{ t("transfer_page.no_standard_products_found") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, onMounted, ref, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import type { Product } from "../../../services/product";
import type { Warehouse } from "../../../services/warehouse";

export default defineComponent({
  name: "TransferChooseForm",
  props: {
    warehouses: {
      type: Array as PropType<Warehouse[]>,
      default: () => [],
    },
    fromWarehouseId: {
      type: [Number, String],
      default: null,
    },
    toWarehouseId: {
      type: [Number, String],
      default: null,
    },
    fromWarehouseLocked: {
      type: Boolean,
      default: false,
    },
    date: {
      type: String,
      default: "",
    },
    referenceNo: {
      type: String,
      default: "",
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    searchResults: {
      type: Array as PropType<Product[]>,
      default: () => [],
    },
    showResults: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:fromWarehouseId", "update:toWarehouseId", "search-product", "add-product"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const searchQuery = ref("");
    const searchInput = ref<HTMLInputElement | null>(null);
    const isFocused = ref(false);

    const normalizedQuery = computed(() => searchQuery.value.replace(/\s+/g, " ").trim());
    const shouldShowResults = computed(() => isFocused.value && props.showResults);
    const sourceWarehouseName = computed(() => {
      const selectedWarehouse = props.warehouses.find((warehouse) => Number(warehouse.id) === Number(props.fromWarehouseId));
      return selectedWarehouse?.name || t("transfer_page.source_warehouse");
    });
    const availableDestinationWarehouses = computed(() =>
      props.warehouses.filter((warehouse) => Number(warehouse.id) !== Number(props.fromWarehouseId))
    );

    const getAvailableStock = (product: Product & { available_stock?: number }) =>
      Number(product.available_stock || 0);

    const selectProduct = (product: Product) => {
      emit("add-product", product);
      searchQuery.value = "";
      isFocused.value = false;
      searchInput.value?.focus();
    };

    const addFirstResult = () => {
      if (props.searchResults.length > 0) {
        selectProduct(props.searchResults[0]);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (searchInput.value?.contains(target)) return;
      const searchContainer = searchInput.value?.closest(".transfer-search-box");
      if (searchContainer?.contains(target)) return;
      isFocused.value = false;
    };

    onMounted(() => {
      document.addEventListener("click", handleClickOutside);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    return {
      searchQuery,
      searchInput,
      isFocused,
      normalizedQuery,
      shouldShowResults,
      sourceWarehouseName,
      availableDestinationWarehouses,
      getAvailableStock,
      selectProduct,
      addFirstResult,
      t,
    };
  },
});
</script>

<style scoped>
.search-results-dropdown {
  position: absolute;
  width: 100%;
  z-index: 1000;
  background: #fff;
  border: 1px solid #e9edf5;
  border-radius: 12px;
  box-shadow: 0 18px 45px rgba(20, 32, 62, 0.12);
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
}

.transfer-search-box {
  position: relative;
}

.transfer-search-input {
  width: 100%;
  height: 55px;
  border: 1px solid #e6ebf2;
  border-radius: 8px;
  background: #fff;
  color: #1f2940;
  font-size: 14px;
  padding: 0 52px 0 16px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.transfer-field {
  background: #fff;
  border: 1px solid #e6ebf2;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.transfer-field:focus {
  outline: none;
  border-color: #5d5fef;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.transfer-field:disabled,
.transfer-field[readonly] {
  background: #fff;
  color: #1f2940;
}

.transfer-search-input:focus {
  outline: none;
  border-color: #5d5fef;
  box-shadow: 0 0 0 3px rgba(93, 95, 239, 0.12);
}

.search-icon {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #8f9bb3;
  pointer-events: none;
  font-size: 18px;
}

.result-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
  background: #f8fbff;
  font-size: 13px;
  color: #58627a;
}

.result-item {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border: 0;
  background: #fff;
  padding: 14px 16px;
  text-align: left;
  transition: background-color 0.2s ease;
  border-bottom: 1px solid #f1f4f8;
}

.result-item:hover {
  background: #f8fbff;
}

.result-main,
.result-side {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.result-name {
  color: #1f2940;
  font-size: 15px;
}

.result-meta {
  color: #7f8aa3;
  font-size: 12px;
  margin-top: 2px;
}

.meta-dot {
  margin: 0 6px;
}

.result-code {
  align-self: flex-end;
  background: #edf4ff;
  color: #3563e9;
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
}

.result-stock {
  margin-top: 8px;
  align-self: flex-end;
  color: #1f9254;
  font-size: 12px;
  font-weight: 600;
}

.result-stock.is-low {
  color: #e5484d;
}

.result-empty-state {
  padding: 18px 16px;
  text-align: center;
  color: #7f8aa3;
  font-size: 14px;
}
</style>
